import { useEffect } from 'react';
import type { JSX } from 'react';
import { injectHeadScript } from '../util/inject-head-script';
import {
    claimBoot,
    getAdvertisingWindow,
    type AdvertisingWindow,
    type OaiqStub,
} from '../util/advertising-window';
import { isConfiguredPixelId } from './is-configured-pixel-id';
/* eslint-disable jsx-a11y/alt-text */

const OAI_SDK_SRC = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';

interface OpenAiTrackerProps {
    /**
     * Primary ChatGPT Ads pixel id. The loader accepts an array so a second
     * advertiser pixel can be added later without restructuring.
     */
    pixelId: string;
}

/**
 * Return `window.oaiq`, creating OpenAI's canonical queueing stub if it does not
 * exist yet. An existing `oaiq` is reused rather than replaced so Business
 * Manager pages that bootstrap the pixel from a Silverstripe template keep a
 * single queue.
 */
const getOrCreateOaiq = (w: AdvertisingWindow): OaiqStub => {
    const existing = w.oaiq;
    if (existing) return existing;

    function oaiq(...args: unknown[]): void {
        oaiq.q.push(args);
    }

    oaiq.q = [] as unknown[][];

    w.oaiq = oaiq;
    return oaiq;
};

/**
 * Boot the ChatGPT Ads Measurement Pixel for every configured id.
 *
 * Empty ids and the `foo` placeholder are skipped. Each id is claimed once per
 * document so effect re-runs do not re-init the same pixel.
 */
export const loadOpenAiPixel = (pixelIds: string[]): void => {
    const w = getAdvertisingWindow();
    const ids = pixelIds
        .filter(isConfiguredPixelId)
        .filter((id) => claimBoot(w, `openai:${id}`));

    if (ids.length === 0) return;

    const oaiq = getOrCreateOaiq(w);
    for (const id of ids) {
        oaiq('init', { pixelId: id });
    }

    injectHeadScript(OAI_SDK_SRC);
};

export const OpenAiPixelHead: React.FC<OpenAiTrackerProps> = ({ pixelId }) => {
    // The pixel is injected after hydration (see `injectHeadScript`).
    useEffect(() => {
        loadOpenAiPixel([pixelId]);
    }, [pixelId]);

    return null;
};

export const OpenAiPixelBody = ({
    pixelId,
}: OpenAiTrackerProps): JSX.Element | null => {
    if (!isConfiguredPixelId(pixelId)) {
        return null;
    }

    return (
        <noscript>
            <img
                height="1"
                width="1"
                style={{ display: 'none' }}
                src={`https://bzr.openai.com/v1/sdk/events?pid=${encodeURIComponent(pixelId)}&event=page_viewed&data[type]=contents`}
            />
        </noscript>
    );
};
